import React from 'react';

import Link from '@/components/Link';

import { useFindExpertsAction } from './useFindExpertsAction';

/**
 * Button that gates expert-request / project creation behind a
 * billing-aware dialog. Three branches handled inside
 * `useFindExpertsAction`:
 *
 *  1. SMB-incomplete viewer (`signup_type==='smb' && !hasTeamBilling`) —
 *     opens SelfServiceSMBSignupModal so they can finish signup. The
 *     modal's deriveResumeStep picks the right step (team-create,
 *     team-join, subscription) based on viewer state.
 *  2. Viewer with at least one group — renders the clickable wrapped in
 *     `<Link to={to}>` so the browser's native cmd/middle-click affordance
 *     opens a new tab. Plain click still navigates in-page via the
 *     react-router-aware Link component.
 *  3. Otherwise — opens TeamAccountPromoDialog (Contact Sales). Correct
 *     destination for non-SMB no-team enterprise leads.
 *
 * signup_type is provenance, not current state — once an SMB user joins
 * a team with billing, isSMBIncomplete flips false and they fall into
 * branch 2. Do not mutate signup_type on team join. See ENG-5251 plan.
 *
 * ENG-5251 (PR #2688 review S1 + S2): the 3-branch logic moved into
 * `useFindExpertsAction` so the AccessHeader nav-menu can share it. The
 * navigate branch wraps the clickable in `<Link to={to}>` to restore
 * the cmd/middle-click "open in new tab" affordance that this PR's
 * earlier CTA refactor removed.
 *
 * (props: any) and the `connect()` HOC have been removed in this round
 * because the hook now reads viewer via `useAppSelector` — no more
 * connected viewer prop, no more dispatch passthrough. The remaining
 * polymorphic `component` prop typing is kept narrow per the explicit
 * deferral of the broader `(props: any)` cleanup.
 */
const TeamAccountPromoButton = (props: any): JSX.Element => {
  const {
    component: ClickableComponent,
    onClick,
    to = '/request_expert',
    dialogProps,
    ...other
  } = props;

  const {
    branch,
    to: navigateTo,
    openSignup,
    openContactSales,
    surface,
  } = useFindExpertsAction({
    to,
    dialogProps,
  });

  const handleClick = () => {
    if (branch === 'smb-signup') {
      openSignup();
      return;
    }
    if (branch === 'contact-sales') {
      openContactSales();
      return;
    }
    // Navigate branch: the <Link> wrapper handles navigation natively.
    // The original `onClick` (if any) is preserved as a side-effect hook
    // (analytics, etc.); React Router's Link will navigate regardless.
    if (onClick) onClick();
  };

  const clickable = (
    <ClickableComponent {...other} onClick={handleClick}>
      {other.label}
      {other.children}
    </ClickableComponent>
  );

  return (
    <React.Fragment>
      {surface}
      {branch === 'navigate' && navigateTo ? <Link to={navigateTo}>{clickable}</Link> : clickable}
    </React.Fragment>
  );
};

export default TeamAccountPromoButton;
